import { useId } from 'react';

import { PATTERNS, coverRecipe, type CoverInputs } from './coverArt';
import styles from './CurateCover.module.css';

/**
 * Generative cover art for a Curate article.
 *
 * Most articles ship without a `headerImageUrl`, and a grid of text-only tiles
 * reads as unfinished. Rather than a placeholder icon, every article gets a
 * piece of art built from four inline-SVG layers: a brand gradient, a soft
 * colored glow, one of eight geometric pattern families, and film grain.
 *
 * Three inputs decide the look, so the art carries meaning instead of being
 * random decoration:
 *
 *   pattern  <- article slug      same article, same art, on every device
 *   palette  <- first topic tag   modules on one subject share a color family
 *   accent   <- assignment reason warm = complaint-driven, mint = RSQ indicator,
 *                                 stone = assigned by a person, amber = tag rule
 *
 * When a real header image exists it fills the same slot and the generator
 * is bypassed entirely. Everything renders as `<span>`s so a cover can live
 * inside a `<button>` card without producing invalid HTML.
 */

/* Component
 * -------------------------------------------------------------------------- */

interface CurateCoverProps extends CoverInputs {
  /** A real header image, when the article has one, replaces the generator. */
  imageUrl?: string | null;
  className?: string;
}

export function CurateCover({
  slug,
  tag,
  reason,
  hero = false,
  imageUrl,
  className,
}: CurateCoverProps) {
  // Gradient/filter ids must be unique per instance: the same article can be
  // on screen twice (hero and list) and SVG resolves url(#id) document-wide.
  const uid = useId().replace(/:/g, '');
  const cls = [styles.cover, className].filter(Boolean).join(' ');

  if (imageUrl) {
    return (
      <span className={cls} aria-hidden="true">
        <img className={styles.image} src={imageUrl} alt="" loading="lazy" />
      </span>
    );
  }

  const { pattern, palette, accent } = coverRecipe({ slug, tag, reason, hero });

  // Hero mode widens the canvas and parks the 100x100 pattern at the right
  // edge. With `xMaxYMid slice` the SVG scales to the card's height on wide
  // cards and to its width on narrow ones, so the pattern stays near thumbnail
  // scale from a 358px phone hero to a 920px desktop one, and the left side
  // is free for the title and scrim.
  const width = hero ? 300 : 100;
  const dx = hero ? 200 : 0;

  return (
    <span className={cls} aria-hidden="true" data-pattern={pattern}>
      <svg viewBox={`0 0 ${width} 100`} preserveAspectRatio="xMaxYMid slice" focusable="false">
        <defs>
          <linearGradient id={`${uid}g`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={palette[0]} />
            <stop offset="1" stopColor={palette[1]} />
          </linearGradient>
          <radialGradient
            id={`${uid}r`}
            cx={hero ? '92%' : '82%'}
            cy="14%"
            r={hero ? '30%' : '70%'}
          >
            <stop offset="0" stopColor={accent} stopOpacity={0.55} />
            <stop offset="0.55" stopColor={accent} stopOpacity={0.12} />
            <stop offset="1" stopColor={accent} stopOpacity={0} />
          </radialGradient>
          <radialGradient id={`${uid}v`} cx="20%" cy="95%" r="80%">
            <stop offset="0" stopColor="#000" stopOpacity={0.28} />
            <stop offset="1" stopColor="#000" stopOpacity={0} />
          </radialGradient>
          <filter id={`${uid}n`} x="0" y="0" width="100%" height="100%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="1.1"
              numOctaves="2"
              stitchTiles="stitch"
            />
            <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 .14 0" />
          </filter>
        </defs>
        <rect width={width} height={100} fill={`url(#${uid}g)`} />
        <rect width={width} height={100} fill={`url(#${uid}r)`} />
        <g transform={`translate(${dx} 0)`}>{PATTERNS[pattern](accent)}</g>
        <rect width={width} height={100} fill={`url(#${uid}v)`} />
        <rect width={width} height={100} filter={`url(#${uid}n)`} opacity={0.9} />
      </svg>
    </span>
  );
}
